import React, { useState, useRef, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Upload, FileSpreadsheet, Database, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function BulkImport() {
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [count, setCount] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [columnMap, setColumnMap] = useState({
    medicineId: "Medicine ID",
    brandName: "Brand Name",
    genericName: "Generic Name",
    composition: "Composition",
    strength: "Strength",
    dosageForm: "Dosage Form",
    manufacturer: "Manufacturer",
    route: "Route",
    standardDose: "Standard Dose",
    frequency: "Frequency",
    timing: "Timing",
    instructions: "Instructions",
  });
  const pollRef = useRef();

  useEffect(() => {
    (async () => {
      const r = await api.get("/medicines/count");
      setCount(r.data.count);
    })();
    return () => clearInterval(pollRef.current);
  }, []);

  const startImport = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("column_map", JSON.stringify(columnMap));
    try {
      const r = await api.post("/medicines/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob({ job_id: r.data.job_id, status: "running", total: 0, inserted: 0, updated: 0, errors: 0 });
      toast.success("Import started");
      poll(r.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed to start");
    }
    setUploading(false);
  };

  const poll = (jid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/medicines/import-job/${jid}`);
        setJob(r.data);
        if (r.data.status === "done") {
          clearInterval(pollRef.current);
          const c = await api.get("/medicines/count");
          setCount(c.data.count);
          toast.success(`Import complete — ${r.data.total} rows processed`);
        }
      } catch (e) { console.debug("[silent]", e); }
    }, 1200);
  };

  const pct = job && job.total ? Math.min(100, Math.round((job.inserted + job.updated) / Math.max(1, job.total) * 100)) : 0;

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <div className="overline">ADMIN</div>
          <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900">Bulk medicine import</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-lg">
            Upload a CSV file containing your medicine catalogue. Rows are upserted by <span className="font-mono text-xs">medicine_id</span>,
            so re-uploading the same file will update in place. The import runs in the background — you can leave the page.
          </p>

          <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
            <div className="section-title">1. Upload CSV</div>
            <label className="mt-3 flex items-center justify-center gap-3 h-32 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-pink-400 hover:bg-pink-50/20" data-testid="csv-drop-zone">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
                data-testid="csv-file-input"
              />
              <FileSpreadsheet size={28} className="text-slate-400" />
              <div>
                <div className="text-sm font-medium">
                  {file ? file.name : "Click to select a CSV file"}
                </div>
                <div className="text-xs text-slate-500">Rows are streamed in batches of 500. UTF-8 encoded.</div>
              </div>
            </label>
          </div>

          <div className="mt-4 bg-white border border-slate-200 rounded-md p-5">
            <div className="section-title">2. Column mapping</div>
            <p className="text-xs text-slate-500 mt-1">Map your CSV column headers to the medicine fields. Defaults follow the recommended schema.</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {Object.keys(columnMap).map((k) => (
                <div key={k}>
                  <Label className="overline">{k}</Label>
                  <Input value={columnMap[k]} onChange={(e) => setColumnMap({ ...columnMap, [k]: e.target.value })} className="h-8 text-sm" data-testid={`colmap-${k}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <Button onClick={startImport} disabled={!file || uploading} className="bg-pink-600 hover:bg-pink-700 text-white gap-2" data-testid="start-import-btn">
              <Upload size={14} /> Start import
            </Button>
          </div>

          {job && (
            <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="import-progress-card">
              <div className="flex items-center justify-between">
                <div className="section-title flex items-center gap-2">
                  {job.status === "done" ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Database size={16} className="text-pink-600 animate-pulse" />}
                  Import job <span className="tabular text-xs text-slate-500 ml-1">{job.job_id}</span>
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">{job.status}</div>
              </div>
              <Progress value={pct} className="mt-4 h-2" data-testid="import-progress-bar" />
              <div className="grid grid-cols-4 gap-3 mt-4 text-center">
                <Stat label="Rows read" value={job.total} />
                <Stat label="Inserted" value={job.inserted} tone="emerald" />
                <Stat label="Updated" value={job.updated} tone="indigo" />
                <Stat label="Errors" value={job.errors} tone={job.errors ? "red" : "slate"} />
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-4">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="overline">CATALOGUE</div>
            <div className="mt-1 font-heading text-3xl font-semibold tabular flex items-center gap-2">
              <Database size={22} className="text-pink-600" />
              {count != null ? count.toLocaleString() : "—"}
            </div>
            <div className="text-xs text-slate-500 mt-1">medicines currently indexed</div>
          </div>
          <div className="mt-4 alert-info text-xs rounded-md p-4 leading-relaxed">
            <strong className="font-semibold">CSV format tip:</strong> include headers on row 1. If a row already
            has a matching <span className="font-mono">medicine_id</span> the record is updated, otherwise a new
            one is inserted. Search indexes rebuild automatically after each batch.
          </div>
          <a
            href="/medicine_import_template.csv"
            download
            className="mt-3 block text-center text-xs font-medium text-pink-700 border border-pink-200 bg-pink-50 hover:bg-pink-100 rounded-md py-2.5"
            data-testid="download-template-link"
          >
            Download CSV template (with example rows)
          </a>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    emerald: "text-emerald-600",
    indigo: "text-pink-600",
    red: "text-red-600",
    slate: "text-slate-700",
  }[tone || "slate"];
  return (
    <div>
      <div className={`font-heading text-2xl font-semibold tabular ${cls}`}>{(value ?? 0).toLocaleString()}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}
